import { useState, useCallback } from 'react';
import { useNotifications } from '@/hooks/useNotifications';
import { useAudioFeedback } from '@/hooks/useAudioFeedback';

type PaymentStatus = 'idle' | 'processing' | 'success' | 'error';

interface CardPaymentResult {
  success: boolean;
  message: string;
}

export const useCardPayment = () => {
  const [status, setStatus] = useState<PaymentStatus>('idle');
  const { success, error } = useNotifications();
  const { playSuccessSound, playErrorSound } = useAudioFeedback();

  const processCardPayment = useCallback(async (amount: number): Promise<CardPaymentResult> => {
    setStatus('processing');

    try {
      // Mock API call à SumUp/Stripe Terminal
      // En réalité, ici on appellerait l'API du terminal de paiement
      await new Promise(resolve => setTimeout(resolve, 2000 + Math.random() * 3000));
      
      // Simulation: 100% de succès
      const isSuccess = true;
      
      if (isSuccess) {
        setStatus('success');
        success({
          title: "Paiement accepté ✅",
          description: `Montant de ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount)} encaissé par carte bancaire`
        });
        playSuccessSound();
        
        // Reset status après 3 secondes
        setTimeout(() => setStatus('idle'), 3000);
        
        return {
          success: true,
          message: 'Paiement accepté via CB'
        };
      } else {
        setStatus('error');
        error({
          title: "Paiement refusé ❌",
          description: "Le paiement par carte a été refusé. Réessayez ou choisissez un autre mode de paiement."
        });
        playErrorSound();
        
        // Reset status après 5 secondes
        setTimeout(() => setStatus('idle'), 5000);
        
        return {
          success: false,
          message: 'Paiement refusé, réessayez ou choisissez un autre mode'
        };
      }
    } catch (err) {
      setStatus('error');
      error({
        title: "Erreur de communication",
        description: "Impossible de communiquer avec le terminal de paiement"
      });
      playErrorSound();
      
      setTimeout(() => setStatus('idle'), 5000);
      
      return {
        success: false,
        message: 'Erreur de communication avec le terminal'
      };
    }
  }, [success, error, playSuccessSound, playErrorSound]);

  return {
    status,
    processCardPayment,
    isProcessing: status === 'processing'
  };
};